import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { Mic, MicOff, Loader2, Sparkles, Edit, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AudioRecorder } from '@/utils/audioRecording';

interface DealAIGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyGeneration: (data: any) => void;
}

interface GeneratedDeal {
  title: string;
  description: string;
  deal_type: string;
  priority: string;
  deal_value: number;
  estimated_completion_date?: string | null;
  requirements?: string;
  target_criteria?: string;
}

export const DealAIGenerationModal: React.FC<DealAIGenerationModalProps> = ({
  isOpen,
  onClose,
  onApplyGeneration
}) => {
  const { toast } = useToast();
  const [textInput, setTextInput] = useState('');
  const [generatedDeal, setGeneratedDeal] = useState<GeneratedDeal | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  
  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  // Audio recorder
  const [audioRecorder, setAudioRecorder] = useState<AudioRecorder | null>(null);

  const startRecording = async () => {
    try {
      const recorder = new AudioRecorder(
        async (audioData) => {
          setIsTranscribing(true);
          try {
            const { data, error } = await supabase.functions.invoke('voice-to-text', {
              body: { audio: audioData }
            });

            if (error) throw error;

            setTextInput(prev => prev + (prev ? ' ' : '') + data.text);
            toast({
              title: "Voice Transcribed",
              description: "Your voice has been converted to text successfully."
            });
          } catch (error) {
            console.error('Transcription error:', error);
            toast({
              title: "Transcription Failed",
              description: "Failed to convert voice to text. Please try again.",
              variant: "destructive"
            });
          } finally {
            setIsTranscribing(false);
          }
        },
        () => setIsRecording(true),
        () => setIsRecording(false),
        (error) => {
          console.error('Recording error:', error);
          const isPermissionError = error.message.toLowerCase().includes('permission') || 
                                   error.message.toLowerCase().includes('notallowed');
          
          toast({
            title: isPermissionError ? "Microphone Permission Required" : "Recording Error",
            description: isPermissionError 
              ? "Please allow microphone access in your browser settings and try again."
              : error.message,
            variant: "destructive"
          });
          setIsRecording(false);
        }
      );

      setAudioRecorder(recorder);
      await recorder.startRecording();
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast({
        title: "Recording Failed",
        description: "Failed to start recording. Please check your microphone.",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (audioRecorder) {
      audioRecorder.stopRecording();
      setAudioRecorder(null);
    }
  };

  const generateDeal = async (description: string) => {
    if (!description.trim()) {
      toast({
        title: "Description Required",
        description: "Please provide a description of your deal.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-deal', {
        body: { description: description.trim() }
      });

      if (error) throw error;

      setGeneratedDeal(data);
      toast({
        title: "Deal Generated",
        description: "Your deal has been generated successfully. Review and apply the changes."
      });
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate deal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const startEditing = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const saveEdit = () => {
    if (generatedDeal && editingField) {
      setGeneratedDeal({
        ...generatedDeal,
        [editingField]: editValue
      });
    }
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const applyGeneration = () => {
    if (generatedDeal) {
      // Add debug logging and ensure marketplace deals don't have talent_profile_id
      console.log('Applying AI-generated deal:', generatedDeal);
      const dealData = {
        ...generatedDeal,
        talent_profile_id: null, // AI deals are marketplace deals by default
        status: 'available', // Set proper status for marketplace deals
        posting_type: 'marketplace',
        deal_value: generatedDeal.deal_value || 0 // Ensure deal_value is present
      };
      onApplyGeneration(dealData);
      onClose();
      reset();
    }
  };

  const reset = () => {
    setTextInput('');
    setGeneratedDeal(null);
    setEditingField(null);
    setEditValue('');
    if (audioRecorder && isRecording) {
      stopRecording();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const dealTypeLabels = {
    sponsorship: 'Sponsorship',
    collaboration: 'Collaboration',
    ambassador: 'Brand Ambassador',
    event: 'Event Partnership'
  };

  const priorityColors = {
    low: 'bg-green-500',
    medium: 'bg-yellow-500',
    high: 'bg-red-500'
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Generate Deal with AI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="textInput">Describe your ideal deal</Label>
              <div className="relative">
                <Textarea
                  id="textInput"
                  placeholder="Describe the type of partnership you're looking for. Include details about deliverables, target audience, timeline, and any specific requirements... (or use the microphone to speak)"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  rows={4}
                  className="resize-none pr-12"
                />
                <Button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                  variant={isRecording ? "destructive" : "ghost"}
                  size="sm"
                  className="absolute bottom-2 right-2 h-8 w-8 p-0"
                >
                  {isRecording ? (
                    <MicOff className="w-4 h-4 animate-pulse" />
                  ) : isTranscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </Button>
              </div>
              {isRecording && (
                <p className="text-sm text-destructive">
                  🎤 Recording... Click the microphone again to stop
                </p>
              )}
              {isTranscribing && (
                <p className="text-sm text-muted-foreground">
                  🔄 Converting speech to text...
                </p>
              )}
            </div>
            <Button 
              onClick={() => generateDeal(textInput)}
              disabled={isGenerating || !textInput.trim()}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Deal
                </>
              )}
            </Button>
          </div>

          {generatedDeal && (
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Generated Deal
                </CardTitle>
                <CardDescription>
                  Review and edit the generated content before applying
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Title</Label>
                  {editingField === 'title' ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 px-3 py-1 border rounded-md text-sm"
                        autoFocus
                      />
                      <Button size="sm" onClick={saveEdit}>
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-2 bg-muted rounded-md">
                      <span className="text-sm">{generatedDeal.title}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing('title', generatedDeal.title)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Deal Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Deal Type</Label>
                    <div className="p-2 bg-muted rounded-md">
                      <Badge variant="outline">
                        {dealTypeLabels[generatedDeal.deal_type as keyof typeof dealTypeLabels] || generatedDeal.deal_type}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Priority</Label>
                    <div className="p-2 bg-muted rounded-md">
                      <Badge 
                        className={`text-white ${priorityColors[generatedDeal.priority as keyof typeof priorityColors] || 'bg-gray-500'}`}
                      >
                        {generatedDeal.priority}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Description</Label>
                  {editingField === 'description' ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={3}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit}>
                          <Check className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative p-2 bg-muted rounded-md">
                      <p className="text-sm pr-8">{generatedDeal.description}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-1 right-1"
                        onClick={() => startEditing('description', generatedDeal.description)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Deal Value & Completion Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Deal Value (USD)</Label>
                    {editingField === 'deal_value' ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 px-3 py-1 border rounded-md text-sm"
                          autoFocus
                        />
                        <Button size="sm" onClick={() => {
                          if (generatedDeal) {
                            setGeneratedDeal({ ...generatedDeal, deal_value: Number(editValue) || 0 });
                          }
                          setEditingField(null);
                          setEditValue('');
                        }}>
                          <Check className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <span className="text-sm font-medium">${(generatedDeal.deal_value || 0).toLocaleString()}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEditing('deal_value', String(generatedDeal.deal_value || 0))}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {generatedDeal.estimated_completion_date && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Estimated Completion</Label>
                      <div className="p-2 bg-muted rounded-md">
                        <span className="text-sm">{new Date(generatedDeal.estimated_completion_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Requirements & Target Criteria */}
                {(generatedDeal.requirements || generatedDeal.target_criteria) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {generatedDeal.requirements && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Requirements</Label>
                        <div className="p-2 bg-muted rounded-md">
                          <p className="text-sm">{generatedDeal.requirements}</p>
                        </div>
                      </div>
                    )}
                    {generatedDeal.target_criteria && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Target Criteria</Label>
                        <div className="p-2 bg-muted rounded-md">
                          <p className="text-sm">{generatedDeal.target_criteria}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button onClick={applyGeneration} className="flex-1">
                    Apply to Deal
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};